/**
 * Форматирование для кабинета.
 *
 * Хранение и обмен — UTC (ADR-0025), на экране — местное время. Перевод делается
 * здесь и только здесь: разнесённый по компонентам, он однажды покажет диспетчеру
 * UTC, и тот отправит курьера на три часа раньше.
 */

export const EMPTY = '—';

const MINUTE_MS = 60_000;

export function formatTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) {
    return EMPTY;
  }

  const value = new Date(iso);

  return Number.isNaN(value.getTime())
    ? EMPTY
    : `${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) {
    return EMPTY;
  }

  const value = new Date(iso);

  if (Number.isNaN(value.getTime())) {
    return EMPTY;
  }

  return `${pad(value.getDate())}.${pad(value.getMonth() + 1)} ${formatTime(iso)}`;
}

/** Минут до момента. Отрицательное значение — момент уже прошёл. */
export function minutesUntil(
  iso: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (iso === null || iso === undefined) {
    return null;
  }

  const target = new Date(iso).getTime();

  return Number.isNaN(target) ? null : Math.round((target - now.getTime()) / MINUTE_MS);
}

/**
 * Отклонение прогноза от дедлайна со знаком.
 *
 * Отрицательное — запас, положительное — опоздание. Знак здесь важнее величины:
 * диспетчер сканирует колонку глазами и ищет плюсы.
 */
export function etaDeltaMinutes(
  etaAt: string | null | undefined,
  deadlineAt: string | null | undefined,
): number | null {
  if (etaAt === null || etaAt === undefined || deadlineAt === null || deadlineAt === undefined) {
    return null;
  }

  const eta = new Date(etaAt).getTime();
  const deadline = new Date(deadlineAt).getTime();

  return Number.isNaN(eta) || Number.isNaN(deadline)
    ? null
    : Math.round((eta - deadline) / MINUTE_MS);
}

const MINUTES_IN_HOUR = 60;

/**
 * Длительность для человека: «25 мин», «2 ч 5 мин», «29 ч». Больше часа в минутах не
 * читается: «ожидает 1737 мин» заставляет считать в уме, сколько это суток.
 */
export function formatMinutes(
  minutes: number,
  units: { readonly minutes: string; readonly hours: string },
): string {
  const sign = minutes < 0 ? '−' : '';
  const value = Math.abs(Math.round(minutes));

  if (value < MINUTES_IN_HOUR) {
    return `${sign}${value} ${units.minutes}`;
  }

  const hours = Math.floor(value / MINUTES_IN_HOUR);
  const rest = value % MINUTES_IN_HOUR;

  return rest === 0
    ? `${sign}${hours} ${units.hours}`
    : `${sign}${hours} ${units.hours} ${rest} ${units.minutes}`;
}

export function withSign(minutes: number): string {
  return minutes > 0 ? `+${minutes}` : String(minutes);
}

/** Инициалы для аватара водителя: «Тошматов Элбек» → «ТЭ». */
export function initials(fullName: string | null | undefined): string {
  if (fullName === null || fullName === undefined || fullName.trim() === '') {
    return EMPTY;
  }

  return fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}
